import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Volume2, Pencil, Save, X } from "lucide-react";
import VoiceChannelSelect from "./VoiceChannelSelect";
import { notifyMeetingsChanged } from "./useMeetings";

// The default meeting room (a Discord VC). Founders, executives and site admins can change it.
export default function MeetingRoomCard({ room, canSetRoom }) {
  const [editing, setEditing] = useState(false);
  const [pick, setPick] = useState({ id: room?.channel_id || "", name: room?.channel_name || "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/meetings/room", { channel_id: pick.id, channel_name: pick.name });
      toast.success(pick.id ? `Meeting room set to ${pick.name || "voice channel"}` : "Meeting room cleared");
      setEditing(false);
      notifyMeetingsChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save the room");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface-card p-6 flex flex-col md:flex-row md:items-center gap-5" data-testid="meeting-room-card">
      <div className="w-12 h-12 shrink-0 flex items-center justify-center bg-[var(--ff-red)]/15 text-[var(--ff-red)]">
        <Volume2 size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="label-eyebrow">▮ MEETING ROOM · DISCORD VOICE</div>
        {editing ? (
          <VoiceChannelSelect value={pick.id} onChange={(id, name) => setPick({ id, name })} defaultLabel="— No room set —" testId="meeting-room-select" />
        ) : (
          <div className="font-display text-2xl mt-1 truncate" data-testid="meeting-room-name">
            {room?.channel_id ? room.channel_name || "Voice channel" : <span className="text-[var(--ff-text-dim)]">No room set yet</span>}
          </div>
        )}
        <div className="font-mono-ui text-[0.65rem] text-[var(--ff-text-dim)] mt-1 uppercase tracking-[0.12em]">
          Every meeting uses this room unless it picks a different one.
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {editing ? (
          <>
            <button onClick={save} disabled={saving} className="btn-primary text-xs disabled:opacity-40" data-testid="meeting-room-save"><Save size={12} /> Save room</button>
            <button onClick={() => setEditing(false)} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid="meeting-room-cancel"><X size={12} /> Cancel</button>
          </>
        ) : (
          <>
            {room?.join_url && (
              <a href={room.join_url} target="_blank" rel="noreferrer" className="btn-primary text-xs" data-testid="meeting-room-join"><Volume2 size={12} /> Join room</a>
            )}
            {canSetRoom && (
              <button onClick={() => { setPick({ id: room?.channel_id || "", name: room?.channel_name || "" }); setEditing(true); }} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid="meeting-room-edit">
                <Pencil size={12} /> {room?.channel_id ? "Change room" : "Set room"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
